import { useState, useEffect, type TouchEvent, useContext } from 'react'
import { MapDataContext } from 'src/store/map/Store'
import { NavigationDataContext } from 'src/store/navigation/Store'

import { type Coordinates, type ControllerDirection, type PlayerCoordinates } from 'src/types'

type UsePlayerControlsProps = {
  handlePlayerMovement: (direction: ControllerDirection) => void
  handleEventsMovement: (coordinates: PlayerCoordinates) => void
}

const usePlayerControls = ({handlePlayerMovement, handleEventsMovement}: UsePlayerControlsProps) => {
  const { navigation } = useContext(NavigationDataContext)
  const { map } = useContext(MapDataContext)
  const { player_x, player_y } = map.counters
  const { map_loaded, movement_blocked } = map.switches

  const [firstMoveDelay] = useState(500)
  const [moveDelay] = useState(100)
  const [isFirstMove, setFirstMove] = useState(true)
  const [movementStartTimestamp, setMovementStartTimestamp] = useState(new Date().getTime())
  const [isTouching, setTouching] = useState(false)
  const [touchStart, setTouchStart] = useState<Coordinates | null>(null)
  const [direction, setDirection] = useState<ControllerDirection | null>(null)


  // desktop movement
  const handleKeyDown = (e: KeyboardEvent) => {
    if (navigation.menu_open || navigation.game_over || navigation.event_open || navigation.combat_open) {
      return
    }
    setDirection(null)
    e.preventDefault() // maybe only prevent the arrows?
    if (e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
      handlePlayerMovement('Up')
    } else if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
      handlePlayerMovement('Right')
    } else if (e.key === 'ArrowDown' || e.key === 's' || e.key === 'S') {
      handlePlayerMovement('Down')
    } else if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
      handlePlayerMovement('Left')
    } else if (e.key === ' ') {
      // skip a turn
      handleEventsMovement({player_x, player_y})
    }
  }
  
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const firstTouch = e.touches[0]
    setTouchStart({x: firstTouch.clientX, y: firstTouch.clientY})
    setDirection(null)
  }

  const handleMove = (direction: ControllerDirection) => {
    if (navigation.menu_open || navigation.game_over || navigation.event_open || navigation.combat_open) {
      return
    }
    const currentTimeStamp = new Date().getTime()
    if (currentTimeStamp - movementStartTimestamp > moveDelay) {
      handlePlayerMovement(direction)
      setDirection(direction)
      setMovementStartTimestamp(currentTimeStamp)
    }
  }

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (isTouching || !touchStart || navigation.menu_open || navigation.game_over || navigation.event_open || navigation.combat_open) {
      return
    }

    setTouching(true)
    const currentTimeStamp = new Date().getTime()
    if (currentTimeStamp - movementStartTimestamp > moveDelay) {
      const currentTouch = e.touches[0]
      const xDiff = touchStart.x - currentTouch.clientX
      const yDiff = touchStart.y - currentTouch.clientY

      let direction: ControllerDirection
      if (Math.abs(xDiff) > Math.abs(yDiff)) {
        direction = xDiff > 0 ? 'Left' : 'Right'
      } else {
        direction = yDiff > 0 ? 'Up' : 'Down'
      }

      handleMove(direction)
    }
  }


  const handleTouchEnd = () => {
    if (touchStart) {
      setTouchStart(null)
      setTouching(false)
      setFirstMove(true)
      // allows immediate movement
      const currentTimeStamp = new Date().getTime()
      setMovementStartTimestamp(currentTimeStamp - moveDelay)
    }
  }

  // continues the movement at an interval
  useEffect(() => {
    if (movement_blocked) { return }
    const delay = isFirstMove ? firstMoveDelay : moveDelay
    const interval = setInterval(() => {
      if (isTouching && direction) {
        handlePlayerMovement(direction)
      }
      if (isFirstMove) {
        setFirstMove(false)
      }
    }, delay)
    
    return () => { clearInterval(interval) }
  }, [isTouching, direction, player_x, player_y, movement_blocked])

  useEffect(() => {
    if (map_loaded) {
      document.addEventListener('keydown', handleKeyDown)
    } else {
      document.removeEventListener('keydown', handleKeyDown)
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  })

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  }
}

export default usePlayerControls
